import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { CHAT } from "@/constants/testIds";
import { Loader2, Send, MessageSquare } from "lucide-react";
import { timeAgo } from "@/lib/format";
import { useAuth } from "@/lib/auth";

export default function ChatPage() {
  const { chatId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const chatsQ = useQuery({
    queryKey: ["chats"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/chats");
      // /chats returns { total, conversations } (Iter-240 hotfix — was
      // returning the raw object, causing `.map is not a function`).
      return data?.conversations || data?.chats || [];
    },
    refetchInterval: 15000,
  });

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Messages" description="Chat with buyers and sellers on Allsale Classified." />
      <Breadcrumbs items={[{ label: "Messages" }]} />
      <h1 className="mt-6 font-display text-3xl md:text-4xl font-bold text-slate-900">Messages</h1>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* thread list */}
        <div data-testid={CHAT.list} className="rounded-2xl bg-white shadow-card overflow-hidden">
          <div className="p-4 border-b border-subtle text-xs font-bold uppercase tracking-widest text-slate-400">Conversations</div>
          {chatsQ.isLoading && <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
          {!chatsQ.isLoading && (chatsQ.data || []).length === 0 && (
            <div className="p-8 text-center text-slate-400 text-sm">
              <MessageSquare className="w-6 h-6 mx-auto mb-2" />
              No conversations yet
            </div>
          )}
          <ul className="max-h-[70vh] overflow-y-auto">
            {(chatsQ.data || []).map((c) => {
              const other = c.buyer_id === user?.id ? c.seller_name : c.buyer_name;
              const active = c.id === chatId;
              return (
                <li key={c.id}>
                  <Link
                    to={`/chat/${c.id}`}
                    className={`block px-4 py-3 border-b border-subtle transition-colors ${active ? "bg-[color:var(--brand-blue-wash)]" : "hover:bg-slate-50"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-slate-800 clamp-1">{c.listing_title || "Chat"}</div>
                      <div className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_message_at || c.updated_at || c.created_at)}</div>
                    </div>
                    <div className="text-xs text-slate-500 clamp-1">{other || "—"}</div>
                    {c.last_message && <div className="mt-1 text-xs text-slate-500 clamp-1">{c.last_message}</div>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* thread */}
        <div className="md:col-span-2">
          {chatId ? <Thread chatId={chatId} /> : (
            <div className="rounded-2xl bg-white shadow-card p-14 text-center text-slate-400">
              <MessageSquare className="w-8 h-8 mx-auto mb-3" />
              Pick a conversation to start replying.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Thread({ chatId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const q = useQuery({
    queryKey: ["chat-messages", chatId],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/chats/${chatId}/messages`);
      return data.messages || data || [];
    },
    refetchInterval: 8000,
  });

  const send = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(`/classifieds/chats/${chatId}/messages`, { message: text.trim() });
      return data;
    },
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["chat-messages", chatId] });
      qc.invalidateQueries({ queryKey: ["chats"] });
    },
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [q.data]);

  return (
    <div data-testid={CHAT.thread} className="rounded-2xl bg-white shadow-card flex flex-col h-[70vh]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3">
        {q.isLoading && <div className="text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>}
        {(q.data || []).map((m, i) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm leading-relaxed ${
                mine ? "bg-[color:var(--brand-blue)] text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"
              }`}>
                {m.message || m.text}
                <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>
                  {timeAgo(m.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); if (text.trim()) send.mutate(); }}
        className="border-t border-subtle p-3 flex items-center gap-2"
      >
        <input
          data-testid={CHAT.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 rounded-full bg-slate-50 border border-transparent focus:border-[color:var(--brand-blue)] px-4 py-2.5 text-sm focus:outline-none"
        />
        <button
          data-testid={CHAT.send}
          type="submit"
          disabled={!text.trim() || send.isPending}
          className="rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white w-10 h-10 grid place-items-center disabled:opacity-60 transition-colors"
        >
          {send.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
